import type { ApiSchemas } from '@rental/contracts/client'
import { format, type StaffMessages } from '@rental/i18n'

type Notifications = ApiSchemas['StaffSystem']['notifications']
type Notice = Notifications['pickup']

/** The setting each line reads, named when it is off so the Owner knows which one to set. */
export const NOTIFICATION_KEYS = {
  pickup: 'notifications.pickup_reminder_hours',
  return: 'notifications.return_reminder_hours',
  late: 'notifications.late_return_minutes',
} as const

/** What the Sistema pane says of the reminders and the staff alerts. */
export interface NoticeLines {
  /** Nothing can be sent without a mail server: the pane says so once, with the badge (D7). */
  noEmail: boolean
  pickup: string
  return: string
  late: string
  alerts: string
  recipients: string
}

/**
 * S7: each notice as the worker last scanned it: not read yet (a worker from
 * before S7), on with its hours or minutes, or off and why; and who would
 * read the staff alerts, or that nobody would.
 */
export function noticeLines(
  notifications: Notifications,
  copy: StaffMessages['system']['notifications'],
): NoticeLines {
  const line = (notice: Notice, key: string, on: (value: number) => string) => {
    if (notice === null) return copy.unknown
    if (notice.state === 'off') {
      return notice.reason === 'no_email'
        ? copy.off.no_email
        : format(copy.off.not_configured, { key })
    }
    return notice.value === null ? copy.on : on(notice.value)
  }
  const hours = (value: number) => format(copy.hoursBefore, { hours: value })
  const { it, en } = notifications.recipients
  return {
    noEmail: [
      notifications.pickup,
      notifications.return,
      notifications.late,
      notifications.alerts,
    ].every((notice) => notice?.state === 'off' && notice.reason === 'no_email'),
    pickup: line(notifications.pickup, NOTIFICATION_KEYS.pickup, hours),
    return: line(notifications.return, NOTIFICATION_KEYS.return, hours),
    late: line(notifications.late, NOTIFICATION_KEYS.late, (minutes) =>
      format(copy.minutesAfter, { minutes }),
    ),
    // The hold and CaRGOS alerts have no setting of their own: on whenever e-mail can leave.
    alerts: line(notifications.alerts, '', () => copy.on),
    recipients: it + en === 0 ? copy.noRecipients : format(copy.recipients, { it, en }),
  }
}
